import { graphApiUrlBase, inspectionUrlBase, vaultUrlBase } from "../dependencies";
import { User } from "../models/User";
import { getRandomInt } from "./utils";

// This should have network-only functions.  
// DB-oriented functions should be in the model or controller.

export class Api {
	constructor(props) {
		this.profile = props.profile;
		this.inspectionUrlBase = inspectionUrlBase;
		this.vaultUrlBase = vaultUrlBase;
	}

	static async vaultLogin(username, password){
		let token = await this.tokenForLogin(username, password);
		let data = await this.userForToken(token);
		return data;
	}

	static async tokenForLogin(username, password) {
		/* ** Login to Get Token ** */
		// NOTE - double-encoding this is ON-PURPOSE!!  Required for special characters in the vault.
		// NOTE ALSO - spaces may not work if the Vault proxy is active (like some dev environments)
		let loginUrl = `${vaultUrlBase}/pwatoken/${encodeURIComponent(encodeURIComponent(username))}`;
		let response = await fetch(loginUrl, {
			method: "POST",
			headers: {
				"Content-Type": "text/plain"
			},
			body: password
		});
		if(!response.ok) {
			throw "Invalid username/password";
		}
		let data = await response.json();
		let token = data["securityToken"]["token"];

		if(token == null) {
			throw "Invalid token returned";
		}

		return token;
	}

	static async userForToken(token) {
		/* ** User Token to get User Info ** */
		let userInfoUrl = `${vaultUrlBase}/user`;
		let response = await fetch(userInfoUrl, {
			headers: {
				"Ticket": token
			}
		});
		if(!response.ok) {
			throw "Unable to get user information from the Vault™";
		}
		let userData = await response.json();

		/* ** Package results into a user object ** */
		let user = User.hydrate(userData["userInfo"]);
		user.token = token;

		return user;		
	}

	async verifyLoginStatus() {
		let finalResult = true;
		try {
			let token = this.profile.token;

			/* ** User Token to get User Info ** */
			let userInfoUrl = `${vaultUrlBase}/user`;
			let response = await fetch(userInfoUrl, {
				headers: {
					"Ticket": token
				}
			});

			if(!response.ok) {
				finalResult = false;
				throw "Unable to get user information from the Vault™";
			}
			let userData = await response.json();
			return userData.userInfo.userName == this.profile.userName;
		} catch(err) {
			console.log(`Error verifying login status: ${err}`);
			return finalResult; // Return true if it was a networking error
		}
	}

	static async verifyMcElroyId(idnumber) {
		let response = await fetch(`${vaultUrlBase}/mcid/${encodeURIComponent(idnumber)}`);
		if(!response.ok) {
			throw "Error checking response";
		}
		if(response.status == 204) {
			throw "Invalid McElroy ID";
		}
		let idInfo = await response.json();
		let mcid = idInfo.mcElroyId;
		if(mcid == null) {
			throw "Invalid McElroy ID";
		}
		if(mcid.equipmentInspectorPermExpires == null) {
			throw "Not qualified on the inspection app or qualification has expired";
		}
		let exp = Date.parse(mcid.equipmentInspectorPermExpires);
		let now = Date.now();
		if(exp < now) {
			throw "Not qualified on the inspection app or qualification has expired";
		}
		return mcid;
	}

	// convenience function
	static forProfile(profile) {
		return new Api({profile: profile});
	}

	// Vault API
	async vaultCall(path, method = "GET", data = null) {
		return await this.generalApiCall(this.vaultUrlBase, path, method, data, {
			cacheBusterParam: "_",
			authorizationHeader: "Ticket"
		})

	}

	// Primary function
	async inspectionCall(path, method = "GET", data = null) {
		return await this.generalApiCall(this.inspectionUrlBase, path, method, data, {
			cacheBusterParam: "cacheBuster",
			authorizationHeader: "Authorization"
		})
	}

	async generalApiCall(basePath, path, method = "GET", data = null, opts = {}) {
		if(!navigator.onLine) {
			throw "Not connected to the Internet";
		}

		if(!!opts.cacheBusterParam) {
			if(!path.includes(`${opts.cacheBusterParam}=`)) {
				let cacheInt = getRandomInt();
				if (path.includes("?")) {
					path = `${path}&${opts.cacheBusterParam}=${cacheInt}`;
				} else {
					path = `${path}?${opts.cacheBusterParam}=${cacheInt}`;
				}
			}
		}

		let url = `${basePath}${path}`
		let hdr = {};
		let params = {
			headers: hdr,
			method: method
		};

		let tok = this.profile?.token;
		if(data != null) {
			hdr["Content-Type"] = "application/json";
		}
		if(tok != null) {
			if(opts.authorizationHeader == "Authorization") {
				hdr["Authorization"] = `Bearer ${tok}`;
			} else {
				hdr[opts.authorizationHeader] = tok;
			}
		}

		if(data != null) {
			params["body"] = JSON.stringify(data);
		}
		let response = await fetch(url, params);
		if(!response.ok) {
			throw "Error communicating with the Vault™";
		}
		if(method != "DELETE" && response.status != 204) {
			let result = await response.json();
			return result;
		} else {
			return null;
		}
	}

	// Asset-based convenience functions
	async getAllAssets(params) {
		let usp = new URLSearchParams(params);
		return await this.inspectionCall(`/v2/assets?${usp.toString()}`, "GET");
	}

	// Convenience functions which don't have a class to attach them to
	async componentLookup(assetNumber, includeArchived) {
		let path = `/v2/components/${encodeURIComponent(assetNumber)}`;
		if(includeArchived) {
			path = path + "?includeArchived=1";
		}
		return await this.inspectionCall(path, "GET");
	}

	async componentLookupByModelSerial(mn, sn) {
		return await this.inspectionCall(`/v2/components/by_model_serial/${encodeURIComponent(mn)}/${encodeURIComponent(sn)}`, "GET");
	}

	async componentLookupByUnique(value) {
		return await this.componentLookup(value, false); // just mirroring this for now - not sure if it should include archived or not
	}

	getUserLogoUrl(userId, useBlank) {
		return `${this.inspectionUrlBase}/v2/photos/logos/for/${userId}?useBlank=${useBlank}`;
	}

	async uploadUserLogo(logoData) {
		return await this.inspectionCall("/v2/photos/logos/mine", "PUT", {
			photoData: logoData
		})
	}

	// GraphQL API
	static async graphQLCall(query) {
		let requestObject = {
			query: query
		};
		let resp = await fetch(`${graphApiUrlBase}/graphql`, {
			headers: {
				"Content-Type": "application/json"
			},
			method: "POST",
			body: JSON.stringify(requestObject)
		});
		return resp;
	}

	static graphQLEscape(value) {
		if(value == null) { return null; }
		let len = value.length;
		let newStr = "";
		for(let i = 0; i < len; i++) {
			let char = value[i];
			if(char == "\n") {
				newStr += "\\n";
			} else if(char == "\\") {
				newStr += "\\\\";
			} else if(char == "\"") {
				newStr += "\\\"";
			} else if(char == "\r") {
				newStr += "\\r";
			} else if(char == "\f") {
				newStr += "\\f";
			} else if(char == "\b") {
				newStr += "\\b";
			} else if(char == "\t") {
				newStr += "\\t";
			} else if(char == "/") {
				newStr += "\\/";
			}
			newStr += char;
		}
		return newStr;
	}

	static async dataForPart(partNumber) {
		if(!partNumber) { return null; }
		let response = await this.graphQLCall(`{ item(partNumber: "${Api.graphQLEscape(partNumber)}") { id description photo } }`);
		let json = await response.json();
		return json.data?.item;
	}

	static async dataForSerial(serialNumber) {
		if(!serialNumber) { return null; }
		let response = await this.graphQLCall(`{ serialMaster(serialNumber: "${Api.graphQLEscape(serialNumber)}") { id  description partNumber inServiceDate shipDate createDate registeredDate } }`);
		let json = await response.json();
		return json.data?.serialMaster;
	}
}